import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import { useTheme } from '../context/ThemeContext';

export default function Sidebar({ onClose, isMobile }) {
  const { user, signOut } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [clients, setClients] = useState([]);
  const [editingVisibility, setEditingVisibility] = useState(false);
  const [hiddenClients, setHiddenClients] = useState(() => {
    try { return JSON.parse(localStorage.getItem('sidebar_hidden') || '[]'); }
    catch { return []; }
  });

  useEffect(() => { loadClients(); }, []);

  async function loadClients() {
    const { data } = await supabase
      .from('clients')
      .select('id, name, ad_accounts(platform)')
      .order('name');
    if (data) setClients(data);
  }

  function toggleHidden(id) {
    const next = hiddenClients.includes(id)
      ? hiddenClients.filter(x => x !== id)
      : [...hiddenClients, id];
    setHiddenClients(next);
    localStorage.setItem('sidebar_hidden', JSON.stringify(next));
  }

  function getUniquePlatforms(adAccounts) {
    return [...new Set((adAccounts || []).map(a => a.platform))];
  }

  function getInitials(name) {
    return name.split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase();
  }

  const activeClientId = new URLSearchParams(location.search).get('client');
  const visibleClients = editingVisibility ? clients : clients.filter(c => !hiddenClients.includes(c.id));

  const navStyle = ({ isActive }) => ({
    display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', borderRadius: 8,
    cursor: 'pointer', fontSize: 13, fontWeight: 500, textDecoration: 'none',
    color: isActive ? '#7c3aed' : 'var(--text2)',
    background: isActive ? (theme === 'dark' ? '#1a1d35' : '#ede9fe') : 'transparent',
    transition: 'all 0.1s',
  });

  function handleClientClick(id) {
    navigate(`/?client=${id}`);
    if (isMobile && onClose) onClose();
  }

  function handleNavClick() {
    if (isMobile && onClose) onClose();
  }

  return (
    <div style={{
      width: 200, background: 'var(--sidebar-bg)', borderRight: '1px solid var(--border)',
      display: 'flex', flexDirection: 'column', flexShrink: 0,
      height: '100vh',
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
    }}>

      {/* Logo + azioni */}
      <div style={{ padding: '18px 16px 14px', display: 'flex', alignItems: 'center', gap: 8, borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
        <div style={{ width: 28, height: 28, background: '#7c3aed', borderRadius: 7, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, color: 'white', flexShrink: 0 }}>G</div>
        <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)', flex: 1 }}>Gallicantus</span>
        <button onClick={toggleTheme} title={theme === 'dark' ? 'Tema chiaro' : 'Tema scuro'}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text3)', padding: 2, display: 'flex', alignItems: 'center', flexShrink: 0 }}>
          {theme === 'dark'
            ? <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.3"><circle cx="7" cy="7" r="3"/><line x1="7" y1="1" x2="7" y2="2.5"/><line x1="7" y1="11.5" x2="7" y2="13"/><line x1="1" y1="7" x2="2.5" y2="7"/><line x1="11.5" y1="7" x2="13" y2="7"/><line x1="2.9" y1="2.9" x2="4" y2="4"/><line x1="10" y1="10" x2="11.1" y2="11.1"/><line x1="11.1" y1="2.9" x2="10" y2="4"/><line x1="4" y1="10" x2="2.9" y2="11.1"/></svg>
            : <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.3"><path d="M11.5 8.5A5 5 0 015.5 2.5a5 5 0 106 6z" strokeLinecap="round"/></svg>
          }
        </button>
        {isMobile && (
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text3)', padding: 2, display: 'flex', alignItems: 'center', flexShrink: 0 }}>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5"><line x1="2" y1="2" x2="12" y2="12"/><line x1="12" y1="2" x2="2" y2="12"/></svg>
          </button>
        )}
      </div>

      {/* Nav principale */}
      <div style={{ padding: '10px 8px 0', display: 'flex', flexDirection: 'column', gap: 2, flexShrink: 0 }}>
        <NavLink to="/" end style={navStyle} onClick={handleNavClick}>
          <svg width="15" height="15" viewBox="0 0 15 15" fill="currentColor" style={{ flexShrink: 0 }}><rect x="1" y="8" width="3" height="6" rx="1"/><rect x="6" y="5" width="3" height="9" rx="1" opacity="0.65"/><rect x="11" y="1" width="3" height="13" rx="1" opacity="0.4"/></svg>
          Genera Report
        </NavLink>
        <NavLink to="/reports" style={navStyle} onClick={handleNavClick}>
          <svg width="15" height="15" viewBox="0 0 15 15" fill="none" stroke="currentColor" strokeWidth="1.3" style={{ flexShrink: 0 }}><rect x="1.5" y="1.5" width="12" height="12" rx="2"/><line x1="4" y1="5" x2="11" y2="5"/><line x1="4" y1="7.5" x2="11" y2="7.5"/><line x1="4" y1="10" x2="8" y2="10"/></svg>
          Report
        </NavLink>
      </div>

      {/* Clienti */}
      <div style={{ marginTop: 18, flex: 1, overflow: 'auto', minHeight: 0 }}>
        <div style={{ padding: '0 16px 6px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.8px', textTransform: 'uppercase', color: 'var(--text3)' }}>
            Clienti {hiddenClients.length > 0 && !editingVisibility && (
              <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0, fontSize: 10 }}>({clients.length - hiddenClients.length}/{clients.length})</span>
            )}
          </span>
          <button
            onClick={() => setEditingVisibility(v => !v)}
            title={editingVisibility ? 'Chiudi' : 'Mostra/nascondi clienti'}
            style={{ background: editingVisibility ? (theme === 'dark' ? '#1a1d35' : '#ede9fe') : 'none', border: 'none', cursor: 'pointer', color: editingVisibility ? '#7c3aed' : 'var(--text3)', padding: '2px 4px', borderRadius: 5, display: 'flex', alignItems: 'center' }}
          >
            <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="currentColor" strokeWidth="1.3">
              {editingVisibility
                ? <><line x1="2" y1="2" x2="11" y2="11"/><line x1="11" y1="2" x2="2" y2="11"/></>
                : <><circle cx="6.5" cy="6.5" r="2"/><path d="M1 6.5C2.5 3.5 4.8 2 6.5 2s4 1.5 5.5 4.5C10.5 9.5 8.2 11 6.5 11S2.5 9.5 1 6.5z"/></>
              }
            </svg>
          </button>
        </div>

        {editingVisibility && (
          <div style={{ margin: '0 8px 8px', padding: '8px 10px', background: 'var(--surface)', borderRadius: 8, border: '1px solid var(--border)' }}>
            <p style={{ fontSize: 11, color: 'var(--text2)', margin: '0 0 8px', lineHeight: 1.4 }}>
              Clicca un cliente per mostrarlo o nasconderlo dalla sidebar.
            </p>
          </div>
        )}

        {visibleClients.map(c => {
          const platforms = getUniquePlatforms(c.ad_accounts);
          const isActive = activeClientId === String(c.id);
          const isHidden = hiddenClients.includes(c.id);
          return (
            <div key={c.id}
              onClick={() => editingVisibility ? toggleHidden(c.id) : handleClientClick(c.id)}
              style={{
                padding: '5px 14px', display: 'flex', alignItems: 'center', gap: 7,
                cursor: 'pointer', borderRadius: 6, margin: '0 4px',
                background: isActive && !editingVisibility ? (theme === 'dark' ? '#13102a' : '#ede9fe') : 'transparent',
                opacity: editingVisibility && isHidden ? 0.4 : 1,
                transition: 'all 0.1s',
              }}
              onMouseEnter={e => { if (!isActive || editingVisibility) e.currentTarget.style.background = 'var(--surface)'; }}
              onMouseLeave={e => { if (!isActive || editingVisibility) e.currentTarget.style.background = 'transparent'; }}
            >
              {editingVisibility ? (
                <div style={{ width: 14, height: 14, borderRadius: 3, border: `1.5px solid ${isHidden ? 'var(--border)' : '#7c3aed'}`, background: isHidden ? 'transparent' : '#7c3aed', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  {!isHidden && <svg width="8" height="7" viewBox="0 0 8 7" fill="none"><path d="M1 3.5L3 5.5L7 1" stroke="white" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                </div>
              ) : (
                <div style={{ width: 5, height: 5, borderRadius: '50%', flexShrink: 0, background: isActive ? '#7c3aed' : 'var(--border)' }} />
              )}
              <span style={{ fontSize: 12, color: isActive && !editingVisibility ? '#7c3aed' : 'var(--text2)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</span>
              <div style={{ display: 'flex', gap: 2, flexShrink: 0 }}>
                {platforms.includes('meta') && <div style={{ width: 14, height: 14, borderRadius: 3, background: 'rgba(24,119,242,0.12)', color: '#4a9eff', fontSize: 8, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>M</div>}
                {platforms.includes('google') && <div style={{ width: 14, height: 14, borderRadius: 3, background: 'rgba(52,168,83,0.12)', color: '#34a853', fontSize: 8, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>G</div>}
                {platforms.includes('tiktok') && <div style={{ width: 14, height: 14, borderRadius: 3, background: 'var(--surface)', color: 'var(--text3)', fontSize: 8, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>T</div>}
              </div>
            </div>
          );
        })}
      </div>

      {/* Settings */}
      <div style={{ borderTop: '1px solid var(--border)', padding: '8px 8px 4px', flexShrink: 0 }}>
        <div style={{ padding: '0 8px 6px', fontSize: 10, fontWeight: 700, letterSpacing: '0.8px', textTransform: 'uppercase', color: 'var(--text3)' }}>Impostazioni</div>
        <NavLink to="/settings/clients" style={navStyle} onClick={handleNavClick}>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.3" style={{ flexShrink: 0 }}><circle cx="7" cy="5" r="2.5"/><path d="M2 12c0-2.2 2.2-4 5-4s5 1.8 5 4" strokeLinecap="round"/></svg>
          Clienti
        </NavLink>
        <NavLink to="/settings/integrations" style={navStyle} onClick={handleNavClick}>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.3" style={{ flexShrink: 0 }}><rect x="1.5" y="5" width="3" height="3" rx="1"/><rect x="9.5" y="5" width="3" height="3" rx="1"/><line x1="4.5" y1="6.5" x2="9.5" y2="6.5"/></svg>
          Integrazioni
        </NavLink>
        <NavLink to="/settings/team" style={navStyle} onClick={handleNavClick}>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.3" style={{ flexShrink: 0 }}><circle cx="5" cy="4.5" r="2"/><path d="M1 12c0-2 1.8-3.5 4-3.5s4 1.5 4 3.5" strokeLinecap="round"/><circle cx="10" cy="4.5" r="1.5"/><path d="M12 11c0-1.5-1-2.5-2-3" strokeLinecap="round"/></svg>
          Team
        </NavLink>
      </div>

      {/* User */}
      {user && (
        <div style={{ padding: '8px 14px 14px', display: 'flex', alignItems: 'center', gap: 8, borderTop: '1px solid var(--border)', flexShrink: 0 }}>
          <div style={{ width: 26, height: 26, borderRadius: '50%', background: 'var(--surface)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 600, color: 'var(--text2)', flexShrink: 0, overflow: 'hidden' }}>
            {user.user_metadata?.avatar_url
              ? <img src={user.user_metadata.avatar_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              : getInitials(user.email || 'U')}
          </div>
          <span style={{ fontSize: 12, color: 'var(--text3)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.email?.split('@')[0]}</span>
          <button onClick={signOut} title="Esci" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text3)', padding: 2, display: 'flex', alignItems: 'center' }}>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.3"><path d="M9 7H2M5 4l-3 3 3 3" strokeLinecap="round" strokeLinejoin="round"/><path d="M7 2h4a1 1 0 011 1v8a1 1 0 01-1 1H7" strokeLinecap="round"/></svg>
          </button>
        </div>
      )}
    </div>
  );
}
